import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  REFERRAL_PIPELINE,
  REFERRAL_STAGE_LABELS,
  type PropertyType,
} from '@valutten/shared-schemas/dist/partnerNetwork';
import { Skeleton, SkeletonStatCards } from '../../../components/common/SkeletonLoader';
import { usePartnerSummary, type PartnerSummaryEntry, type PayoutCard } from '../../../hooks/usePartnerPortal';
import { aggregateSummaries, pipelineCounts, recentActivity, referralDetailPath } from '../../../utils/partnerPortal';
import { formatCentsAud, formatReferralDate } from '../../../utils/partnerReferrals';
import { IncomeStepChart } from '../IncomeStepChart';
import { StagePill } from '../ReferralsTable';
import { usePartnerPortalContext } from '../portalContext';

const CARD = 'rounded-xl border border-gray-700 bg-gray-800/40 p-4 sm:p-6';
const TITLE = 'text-sm font-semibold text-gray-200';

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  investment: 'investment',
  owner_occupied: 'owner-occupied',
  commercial: 'commercial',
};

const bandLabel = (fromCents: number, upToCents: number | null) =>
  upToCents === null ? `Above ${formatCentsAud(fromCents - 1)}` : `Up to ${formatCentsAud(upToCents)}`;

/**
 * The referrer's fee card for one partner: fixed payouts show their amount,
 * a band table shows each band, and a share-of-partner-fee payout shows only
 * its rule text (the server never sends an amount that would reveal the
 * partner's own fee).
 */
const PayoutCardView: React.FC<{ partnerName: string; card: PayoutCard }> = ({ partnerName, card }) => {
  const loadedLabel = card.loading
    ? `${card.loading.propertyTypes.map((t) => PROPERTY_TYPE_LABELS[t] ?? t).join(', ')} (+${card.loading.rateBps / 100}%)`
    : null;
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-wide text-gray-400">{partnerName}</p>
      {card.stages.map((s) => (
        <div key={s.stage} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-200">{s.label}</span>
            {typeof s.flatIncGstCents === 'number' && (
              <span className="text-sm font-medium text-gray-100">{formatCentsAud(s.flatIncGstCents)}</span>
            )}
          </div>
          {s.bands && s.bands.length > 0 ? (
            <table className="w-full rounded-lg bg-gray-900/60 text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">Purchase price</th>
                  <th className="px-3 py-1.5 text-right font-medium">You earn</th>
                  {loadedLabel && <th className="px-3 py-1.5 text-right font-medium">{loadedLabel}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/70">
                {s.bands.map((b) => (
                  <tr key={b.fromCents}>
                    <td className="px-3 py-1.5 text-gray-400">{bandLabel(b.fromCents, b.upToCents)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-100">{formatCentsAud(b.payoutIncGstCents)}</td>
                    {loadedLabel && (
                      <td className="px-3 py-1.5 text-right text-gray-100">
                        {typeof b.loadedPayoutIncGstCents === 'number' ? formatCentsAud(b.loadedPayoutIncGstCents) : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            typeof s.flatIncGstCents !== 'number' && <p className="text-xs text-gray-400">{s.description}</p>
          )}
        </div>
      ))}
    </div>
  );
};

type RecentRow = PartnerSummaryEntry['recent'][number];

const OverviewTab: React.FC = () => {
  const navigate = useNavigate();
  const { referrals, loading, referralsError, hideFees } = usePartnerPortalContext();
  const summary = usePartnerSummary();

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonStatCards count={3} columns={3} />
        <Skeleton height="10rem" />
      </div>
    );
  }
  if (referralsError) {
    return (
      <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
        Could not load your referrals. {referralsError}
      </div>
    );
  }

  const entries = summary.data?.partners ?? null;
  const agg = entries ? aggregateSummaries(entries) : null;
  // Counts and activity fall back to the referral list, so the Overview still works if the summary is unavailable.
  const byStage = agg?.byStage ?? pipelineCounts(referrals);
  const referred = agg?.referred ?? referrals.length;
  const settled = agg?.settled ?? byStage.settled;
  const recent: RecentRow[] =
    agg?.recent ??
    recentActivity(referrals, 5).map((r) => ({
      referralId: r.id,
      clientName: r.clientName,
      to: r.stage,
      at: (r.stageChangedAt ?? r.createdAt) as string | null,
      feeIncGstCents: null,
    }));
  const cards = (entries ?? []).filter((e) => e.payoutCard && e.payoutCard.stages.length > 0);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className={`${CARD} space-y-3 lg:col-span-5`}>
        <h2 className={TITLE}>{hideFees ? 'Referrals' : 'Referral income (inc GST)'}</h2>
        {!hideFees &&
          (summary.loading ? (
            <Skeleton height="2.25rem" width="9rem" />
          ) : (
            <p className="text-3xl font-semibold text-gray-100" data-testid="fees-earned">
              {typeof agg?.feesEarnedIncGstCents === 'number' ? formatCentsAud(agg.feesEarnedIncGstCents) : '—'}
            </p>
          ))}
        <p className="flex items-center gap-2 text-sm text-gray-400">
          <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
          {settled} settled of {referred} referred
        </p>
        {!hideFees && agg && <IncomeStepChart steps={agg.incomeSteps} />}
        {summary.data?.truncated && (
          <p className="text-xs text-amber-300">Totals cover your 500 most recent referrals.</p>
        )}
      </div>

      <div className={`${CARD} space-y-4 lg:col-span-7`}>
        <h2 className={TITLE}>Pipeline</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
          {[...REFERRAL_PIPELINE, 'lapsed' as const].map((stage) => (
            <div key={stage} className="rounded-lg bg-gray-900/60 px-3 py-2">
              <p
                className={`text-xl font-semibold ${
                  stage === 'settled' && byStage[stage] > 0 ? 'text-green-400' : byStage[stage] === 0 ? 'text-gray-500' : 'text-gray-100'
                }`}
              >
                {byStage[stage]}
              </p>
              <p className="text-xs text-gray-400">{REFERRAL_STAGE_LABELS[stage]}</p>
            </div>
          ))}
        </div>
      </div>

      <div className={`${CARD} space-y-2 lg:col-span-7`}>
        <h2 className={TITLE}>Recent activity</h2>
        {recent.length === 0 ? (
          <p className="text-sm text-gray-400">No referrals yet. Refer a client to get started.</p>
        ) : (
          <ul className="divide-y divide-gray-700/70">
            {recent.map((r, i) => (
              <li key={`${r.referralId}:${r.to}:${i}`}>
                <button
                  type="button"
                  onClick={() => navigate(referralDetailPath(r.referralId))}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left hover:bg-gray-800/40"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-100">{r.clientName}</p>
                    <p className="truncate text-xs text-gray-400">Moved to {REFERRAL_STAGE_LABELS[r.to] ?? r.to}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {!hideFees && typeof r.feeIncGstCents === 'number' ? (
                      <span className="text-sm font-medium text-gray-100">{formatCentsAud(r.feeIncGstCents)}</span>
                    ) : (
                      <StagePill stage={r.to} />
                    )}
                    <span className="text-xs text-gray-400">{formatReferralDate(r.at)}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!hideFees && (
        <div className="grid grid-cols-1 gap-6 lg:col-span-5">
          <div className={`${CARD} space-y-2`}>
            <h2 className={TITLE}>Next payment</h2>
            {summary.loading ? (
              <Skeleton height="2rem" width="8rem" />
            ) : summary.error ? (
              <p className="text-sm text-gray-400">Not available right now.</p>
            ) : agg?.nextPayment ? (
              <>
                <p className="text-2xl font-semibold text-gray-100" data-testid="next-payment">
                  {formatCentsAud(agg.nextPayment.amountIncGstCents)}
                </p>
                <p className="text-sm text-gray-400">
                  {agg.nextPayment.count} {agg.nextPayment.count === 1 ? 'fee' : 'fees'} owed to you, paid directly by the
                  partner
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/partner-referrals/rewards')}
                  className="text-sm font-medium text-blue-300 hover:text-blue-200"
                >
                  Take it as a gift card
                </button>
              </>
            ) : (
              <p className="text-sm text-gray-400">Nothing owed right now.</p>
            )}
          </div>

          <div className={`${CARD} space-y-4`}>
            <h2 className={TITLE}>What you are paid (inc GST)</h2>
            {summary.loading ? (
              <Skeleton height="4rem" />
            ) : summary.error ? (
              <p className="text-sm text-gray-400">Not available right now.</p>
            ) : cards.length === 0 ? (
              <p className="text-sm text-gray-400">The partner has not published a fee card yet.</p>
            ) : (
              cards.map((e) => <PayoutCardView key={e.partner.partnerId} partnerName={e.partner.name} card={e.payoutCard!} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
